import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon, DocumentArrowDownIcon } from '@heroicons/react/20/solid';
import { toast } from 'react-toastify';
import { generateJobsReport } from '../utils/jobsReportUtils';
import { StatusChip } from '../components/ui';
import MaterialDrawer from '../components/MaterialDrawer';


function Jobs() {
  document.title = 'All Jobs';

  const { user } = useAuth();
  const [jobsData, setJobsData] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shops, setShops] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterShop, setFilterShop] = useState('');
  const [showShopDropdown, setShowShopDropdown] = useState(false);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filterJobType, setFilterJobType] = useState('');
  const [showJobTypeDropdown, setShowJobTypeDropdown] = useState(false);
  const [filterMaterial, setFilterMaterial] = useState('');
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false);
  const [sortField, setSortField] = useState('expected_delivery_date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [excludeClosed, setExcludeClosed] = useState(true);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [materialDrawerOpen, setMaterialDrawerOpen] = useState(false);
  const [selectedJobForMaterial, setSelectedJobForMaterial] = useState(null);



  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
  
      setLoading(true);
      try {
        const { data: jobs, error: jobsError } = await supabase
          .from('jobs')
          .select(`
            id, po_number, po_line_number, part_number, description, status,
            quantity_on_order, produced_quantity, shipped_quantity,
            expected_delivery_date, kanban_job_id, material_status, material_notes,
            shops(id, name), customers(id, name, nickname),
            parts!inner(part_number, part_number_master, revision, inventory_quantity),
            kanban_jobs(po_number)
          `)
          .order('expected_delivery_date', { ascending: true, nullsFirst: false })
          .order('po_number, po_line_number');
  
        if (jobsError) throw jobsError;
  
        // Filter out Kanban PO line items (no due date AND no kanban_job_id)
        // These are managed via Kanban POs, not the All Jobs view
        const filteredByType = jobs.filter(job =>
          job.expected_delivery_date || job.kanban_job_id
        );

        const formattedJobs = filteredByType.map(job => ({
          id: job.id,
          po_number: job.po_number,
          po_line_number: job.po_line_number,
          part_number: job.part_number,
          description: job.description,
          status: job.status,
          quantity_on_order: job.quantity_on_order,
          produced_quantity: job.produced_quantity,
          shipped_quantity: job.shipped_quantity,
          remaining_to_produce: Math.max(0, job.quantity_on_order - job.produced_quantity - (job.parts?.inventory_quantity || 0)),
          remaining_to_ship: Math.max(0, job.quantity_on_order - job.shipped_quantity),
          expected_delivery_date: job.expected_delivery_date,
          shop_name: job.shops?.name || '-',
          customer_name: job.customers?.nickname || job.customers?.name || '-',
          inventory_quantity: job.parts?.inventory_quantity || 0,
          job_type: job.kanban_job_id ? 'Work Order' : 'Job',
          parent_po: job.kanban_jobs?.po_number || null,
          material_status: job.material_status || null,
          material_notes: job.material_notes || '',
        }));
  
        setJobsData(formattedJobs);
        handleFilter(formattedJobs); // Apply filter with fresh data

        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw shopsError;
        setShops(shopsData || []);

        const { data: customersData, error: customersError } = await supabase
          .from('customers')
          .select('id, name, nickname')
          .order('name');
        if (customersError) throw customersError;
        setCustomers(customersData || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(`Failed to fetch jobs data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [user]);

const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sortedJobs = [...filteredJobs].sort((a, b) => {
      const aValue = a[field] || '';
      const bValue = b[field] || '';
      if (field === 'expected_delivery_date') {
        const aDate = aValue ? new Date(aValue) : new Date(0);
        const bDate = bValue ? new Date(bValue) : new Date(0);
        return newDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }
      if (typeof aValue === 'number')
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      return newDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
    setFilteredJobs(sortedJobs);
  };

  const handleFilter = (data = jobsData) => {
    let filtered = [...data];
    if (filterStatus.length > 0) {
      filtered = filtered.filter(job => filterStatus.includes(job.status));
    }
    if (filterShop) {
      filtered = filtered.filter(job => job.shop_name === filterShop);
    }
    if (filterCustomer) {
      filtered = filtered.filter(job => job.customer_name === filterCustomer);
    }
    if (filterJobType) {
      filtered = filtered.filter(job => job.job_type === filterJobType);
    }
    if (filterMaterial) {
      if (filterMaterial === 'unset') {
        filtered = filtered.filter(job => !job.material_status);
      } else {
        filtered = filtered.filter(job => job.material_status === filterMaterial);
      }
    }
    if (searchQuery) {
      filtered = filtered.filter(job =>
        job.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.part_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.customer_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (excludeClosed) {
      filtered = filtered.filter(job => job.status !== 'closed');
    }
    setFilteredJobs(filtered);
  };

  const openMaterialDrawer = (job) => {
    setSelectedJobForMaterial(job);
    setMaterialDrawerOpen(true);
  };

  const handleMaterialUpdate = (updatedJob) => {
    const updateJob = (job) =>
      job.id === updatedJob.id
        ? { ...job, material_status: updatedJob.material_status, material_notes: updatedJob.material_notes }
        : job;

    setJobsData(prev => prev.map(updateJob));
    setFilteredJobs(prev => prev.map(updateJob));
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    try {
      // Use filtered jobs data for the report
      await generateJobsReport(filteredJobs);
      toast.success('Jobs report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(`Failed to generate report: ${error.message}`);
    } finally {
      setIsGeneratingReport(false);
    }
  };
  

  useEffect(() => {
    handleFilter();
  }, [filterStatus, filterShop, filterCustomer, filterJobType, filterMaterial, searchQuery, excludeClosed]);
  


  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <p className="text-slate-500">Loading...</p>
    </div>
  );

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 h-12 px-4 flex items-center">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-slate-800">All Jobs</h1>
            <span className="text-xs text-slate-500">{filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search PO, part, customer..."
              className="w-56 px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-slate-50"
            />
            <Listbox value={filterStatus} onChange={setFilterStatus} multiple>
              <div className="relative">
                <Listbox.Button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[130px] justify-between">
                  <span className={filterStatus.length > 0 ? 'text-slate-800' : 'text-slate-500'}>
                    {filterStatus.length > 0 ? `${filterStatus.length} selected` : 'All Statuses'}
                  </span>
                  <ChevronDownIcon className="w-4 h-4 text-slate-400" />
                </Listbox.Button>
                <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-sm bg-white py-1 text-sm shadow-lg ring-1 ring-slate-200 focus:outline-none">
                  {[
                    'not_started',
                    'in_production',
                    'partially_shipped',
                    'ready_for_shipment',
                    'closed'
                  ].map((status) => (
                    <Listbox.Option
                      key={status}
                      value={status}
                      className={({ active }) =>
                        `relative cursor-default select-none py-1.5 pl-8 pr-4 ${
                          active ? 'bg-slate-100 text-slate-900' : 'text-slate-700'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {status.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-slate-600">
                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
            <div className="relative">
              <button
                onClick={() => setShowShopDropdown(!showShopDropdown)}
                onBlur={() => setTimeout(() => setShowShopDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[120px] justify-between"
              >
                <span className={filterShop ? 'text-slate-800' : 'text-slate-500'}>
                  {filterShop || 'All Shops'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showShopDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[150px]">
                  <div
                    onClick={() => { setFilterShop(''); setShowShopDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterShop ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Shops
                  </div>
                  {shops.map(shop => (
                    <div
                      key={shop.id}
                      onClick={() => { setFilterShop(shop.name); setShowShopDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterShop === shop.name ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {shop.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[140px] justify-between"
              >
                <span className={filterCustomer ? 'text-slate-800' : 'text-slate-500'}>
                  {filterCustomer || 'All Customers'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showCustomerDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[180px] max-h-64 overflow-y-auto">
                  <div
                    onClick={() => { setFilterCustomer(''); setShowCustomerDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterCustomer ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Customers
                  </div>
                  {customers.map(customer => (
                    <div
                      key={customer.id}
                      onClick={() => { setFilterCustomer(customer.nickname || customer.name); setShowCustomerDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterCustomer === (customer.nickname || customer.name) ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {customer.nickname || customer.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setShowJobTypeDropdown(!showJobTypeDropdown)}
                onBlur={() => setTimeout(() => setShowJobTypeDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[120px] justify-between"
              >
                <span className={filterJobType ? 'text-slate-800' : 'text-slate-500'}>
                  {filterJobType || 'All Types'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showJobTypeDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[130px]">
                  <div
                    onClick={() => { setFilterJobType(''); setShowJobTypeDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterJobType ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Types
                  </div>
                  <div
                    onClick={() => { setFilterJobType('Job'); setShowJobTypeDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterJobType === 'Job' ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    Job
                  </div>
                  <div
                    onClick={() => { setFilterJobType('Work Order'); setShowJobTypeDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterJobType === 'Work Order' ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    Work Order
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setShowMaterialDropdown(!showMaterialDropdown)}
                onBlur={() => setTimeout(() => setShowMaterialDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[120px] justify-between"
              >
                <span className={filterMaterial ? 'text-slate-800' : 'text-slate-500'}>
                  {filterMaterial === 'no_stock' ? 'No Stock' :
                   filterMaterial === 'ordered' ? 'Ordered' :
                   filterMaterial === 'ready' ? 'Ready' :
                   filterMaterial === 'unset' ? 'Not Set' : 'All Material'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showMaterialDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[130px]">
                  <div
                    onClick={() => { setFilterMaterial(''); setShowMaterialDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterMaterial ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Material
                  </div>
                  <div
                    onClick={() => { setFilterMaterial('no_stock'); setShowMaterialDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 flex items-center gap-2 ${filterMaterial === 'no_stock' ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-red-500" /> No Stock
                  </div>
                  <div
                    onClick={() => { setFilterMaterial('ordered'); setShowMaterialDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 flex items-center gap-2 ${filterMaterial === 'ordered' ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-yellow-500" /> Ordered
                  </div>
                  <div
                    onClick={() => { setFilterMaterial('ready'); setShowMaterialDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 flex items-center gap-2 ${filterMaterial === 'ready' ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    <span className="w-2 h-2 rounded-full bg-green-500" /> Ready
                  </div>
                  <div
                    onClick={() => { setFilterMaterial('unset'); setShowMaterialDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterMaterial === 'unset' ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    Not Set
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setExcludeClosed(!excludeClosed)}
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                excludeClosed
                  ? 'bg-slate-700 text-white'
                  : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              Hide Closed
            </button>
            <button
              onClick={handleGenerateReport}
              disabled={isGeneratingReport}
              className="flex items-center px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-sm hover:bg-slate-200 transition-colors disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              {isGeneratingReport ? (
                <>
                  <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="w-4 h-4 mr-1.5" />
                  Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="bg-white border-b border-slate-200">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('expected_delivery_date')}
                >
                  <div className="flex items-center gap-1">Due {sortField === 'expected_delivery_date' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('job_type')}
                >
                  <div className="flex items-center gap-1">Type {sortField === 'job_type' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('customer_name')}
                >
                  <div className="flex items-center gap-1">Customer {sortField === 'customer_name' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('po_number')}
                >
                  <div className="flex items-center gap-1">
                    {filterJobType === 'Work Order' ? 'WO #' : filterJobType === 'Job' ? 'PO #' : 'PO / WO #'}
                    {sortField === 'po_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('po_line_number')}
                >
                  <div className="flex items-center gap-1">Ln {sortField === 'po_line_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('part_number')}
                >
                  <div className="flex items-center gap-1">Part {sortField === 'part_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">Status {sortField === 'status' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('quantity_on_order')}
                >
                  <div className="flex items-center gap-1">Ord {sortField === 'quantity_on_order' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('inventory_quantity')}
                >
                  <div className="flex items-center gap-1">Inv {sortField === 'inventory_quantity' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('produced_quantity')}
                >
                  <div className="flex items-center gap-1">Prod {sortField === 'produced_quantity' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('remaining_to_produce')}
                >
                  <div className="flex items-center gap-1">Rem {sortField === 'remaining_to_produce' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('material_status')}
                >
                  <div className="flex items-center gap-1">Material {sortField === 'material_status' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
                <th
                  className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                  onClick={() => handleSort('shop_name')}
                >
                  <div className="flex items-center gap-1">Shop {sortField === 'shop_name' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}</div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {filteredJobs.map((job) => {
                const isPastDue = job.expected_delivery_date && new Date(job.expected_delivery_date) < new Date();
                return (
                <tr
                  key={job.id}
                  className={`transition-colors duration-100 ${
                    isPastDue
                      ? 'bg-red-50 hover:bg-red-100'
                      : 'hover:bg-slate-50'
                  }`}
                >
                  <td className={`px-3 py-2 whitespace-nowrap text-sm font-medium ${isPastDue ? 'text-red-700' : 'text-slate-700'}`}>
                    {job.expected_delivery_date
                      ? new Date(job.expected_delivery_date).toLocaleDateString('en-US', { timeZone: 'UTC' })
                      : <span className="text-slate-400 font-normal">KANBAN</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm font-medium ${
                      job.job_type === 'Work Order'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}>
                      {job.job_type === 'Work Order' ? 'WO' : 'Job'}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{job.customer_name}</td>
                  <td className="px-3 py-2 text-sm font-medium">
                    <Link to={`/jobs/${job.po_number}`} className="text-slate-700 hover:text-slate-900 hover:underline">
                      {job.po_number}
                    </Link>
                    {job.parent_po && (
                      <div className="text-xs text-slate-400 font-normal">
                        <Link to={`/jobs/${job.parent_po}`} className="hover:text-slate-600 hover:underline">
                          → {job.parent_po}
                        </Link>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700">{job.po_line_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 font-mono">{job.part_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <StatusChip status={job.status} size="xs" />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 tabular-nums">{job.quantity_on_order}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 tabular-nums">{job.inventory_quantity}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-700 tabular-nums">{job.produced_quantity}</td>
                  <td className={`px-3 py-2 whitespace-nowrap text-sm tabular-nums font-medium ${job.remaining_to_produce > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                    {job.remaining_to_produce}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    <button
                      onClick={(e) => { e.stopPropagation(); openMaterialDrawer(job); }}
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm font-medium cursor-pointer transition-colors ${
                        job.material_status === 'no_stock'
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : job.material_status === 'ordered'
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : job.material_status === 'ready'
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                      title={job.material_notes || 'Click to edit material info'}
                    >
                      {job.material_status === 'no_stock' ? (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                          No Stock
                        </>
                      ) : job.material_status === 'ordered' ? (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                          Ordered
                        </>
                      ) : job.material_status === 'ready' ? (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          Ready
                        </>
                      ) : (
                        '—'
                      )}
                    </button>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{job.shop_name}</td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>

      {/* Material Drawer */}
      <MaterialDrawer
        isOpen={materialDrawerOpen}
        onClose={() => setMaterialDrawerOpen(false)}
        job={selectedJobForMaterial}
        onUpdate={handleMaterialUpdate}
      />
    </div>
  );
}

export default Jobs;
